import { useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CheckCircle, PaperPlaneTilt, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Rotating pastel palette (matches PDF chip palette)
const CHIP_PALETTES = [
  { bg: "bg-cyan-100",    text: "text-cyan-800",    ring: "ring-cyan-300",    border: "border-cyan-200"    },
  { bg: "bg-violet-100",  text: "text-violet-800",  ring: "ring-violet-300",  border: "border-violet-200"  },
  { bg: "bg-emerald-100", text: "text-emerald-800", ring: "ring-emerald-300", border: "border-emerald-200" },
  { bg: "bg-pink-100",    text: "text-pink-800",    ring: "ring-pink-300",    border: "border-pink-200"    },
  { bg: "bg-blue-100",    text: "text-blue-800",    ring: "ring-blue-300",    border: "border-blue-200"    },
  { bg: "bg-amber-100",   text: "text-amber-800",   ring: "ring-amber-300",   border: "border-amber-200"   },
];

/**
 * Multiselect-Chips mit Pastell-Farbrotation.
 * `locked=true` sperrt alle nicht-aktiven Chips (z.B. wenn ein Hauptgericht bereits gewählt ist).
 */
export function ChipGroup({ label, options, value, onChange, testid, icon: Icon, locked = false, prices = {} }) {
  const toggle = (o) => onChange(value.includes(o) ? value.filter(x => x !== o) : [...value, o]);
  const fmtPrice = (p) => (p === null || p === undefined || p === "") ? null
    : `+${Number(p).toFixed(2).replace(".", ",")} €`;
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && (
          <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0">
            <Icon size={18} weight="duotone" />
          </div>
        )}
        <div className="text-sm text-slate-700 font-semibold">{label}</div>
      </div>
      <div className="flex flex-wrap gap-2 pl-0 md:pl-10">
        {options.map((o, i) => {
          const active = value.includes(o);
          const disabled = locked && !active;
          const p = CHIP_PALETTES[i % CHIP_PALETTES.length];
          const priceLabel = fmtPrice(prices[o]);
          const cls = active
            ? `${p.bg} ${p.text} border-transparent ring-2 ${p.ring} shadow-sm`
            : disabled
              ? "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed line-through"
              : `bg-white text-slate-500 ${p.border} hover:${p.bg} hover:${p.text}`;
          return (
            <button
              key={o}
              type="button"
              onClick={() => !disabled && toggle(o)}
              disabled={disabled}
              aria-disabled={disabled}
              data-testid={`chip-${o.replace(/\s+/g, "-").toLowerCase()}`}
              className={`text-sm px-3.5 py-1.5 rounded-full border font-medium transition-all inline-flex items-center gap-1.5 ${cls}`}
            >
              {active && <CheckCircle size={12} weight="fill" className="-mt-0.5"/>}
              <span>{o}</span>
              {priceLabel && (
                <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-white/60" : "bg-amber-100 text-amber-700 border border-amber-200"}`}>
                  {priceLabel}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Formular-Rahmen im Jotform-Stil: heller Lavendel-Header + weiße Karte. */
export function FormShell({ title, subtitle, children }) {
  const today = new Date();
  const wd = ["Sonntag","Montag","Dienstag","Mittwoch","Donnerstag","Freitag","Samstag"][today.getDay()];
  const m  = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"][today.getMonth()];
  const longDate = `${wd}, ${m} ${today.getDate()}, ${today.getFullYear()}`;

  // Höhe an die Elterngesellschaft (Strato-iframe-Parent) melden — für Auto-Resize.
  useEffect(() => {
    if (window.parent === window) return;
    const post = () => {
      try {
        const h = document.documentElement.scrollHeight;
        window.parent.postMessage({ __msbFormHeight: h }, "*");
      } catch (_) {}
    };
    post();
    const ro = new ResizeObserver(post);
    ro.observe(document.documentElement);
    window.addEventListener("load", post);
    const iv = setInterval(post, 1500);
    return () => { ro.disconnect(); window.removeEventListener("load", post); clearInterval(iv); };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-3">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Header wie Jotform: helles Indigo */}
          <div className="bg-indigo-50 px-6 pt-6 pb-5">
            <div className="text-xs text-slate-500 text-right mb-2">{longDate}</div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 leading-tight">{title}</h1>
            {subtitle && <div className="text-sm text-slate-500 mt-1">{subtitle}</div>}
          </div>
          <div className="p-6">{children}</div>
        </div>
        <div className="text-center text-xs text-slate-400 mt-4">MainSpessartBlick · Bergstraße</div>
      </div>
    </div>
  );
}

export function submitForm({ form_type, data, guest_name, room_hint, setSaving, onDone }) {
  setSaving(true);
  return axios.post(`${API}/api/public/forms/submit`, { form_type, data, guest_name, room_hint })
    .then(() => { toast.success("Danke! Die Bestellung wurde gesendet."); onDone?.(); })
    .catch(e => toast.error(e.response?.data?.detail || "Bitte erneut versuchen"))
    .finally(() => setSaving(false));
}

export function SubmitButton({ saving, label = "Absenden" }) {
  return (
    <Button type="submit" disabled={saving} data-testid="form-submit-btn"
            className="w-full mt-6 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-base py-6 shadow-sm">
      {saving ? <Spinner size={20} className="animate-spin"/> : <PaperPlaneTilt size={20} weight="fill"/>}
      <span className="ml-2 font-semibold">{label}</span>
    </Button>
  );
}

export function DoneScreen({ label = "Wir haben Ihre Angaben erhalten." }) {
  return (
    <FormShell title="Vielen Dank">
      <div className="text-center py-8">
        <CheckCircle size={64} weight="fill" className="mx-auto text-emerald-500 mb-3"/>
        <div className="font-bold text-lg text-slate-800">Erfolgreich gesendet</div>
        <p className="text-sm text-slate-500 mt-2">{label}</p>
      </div>
    </FormShell>
  );
}

/** Einheitliches Text-Feld im Jotform-Stil. */
export function FieldRow({ label, required, children }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-2 md:gap-4 md:items-center py-3 border-b border-slate-100 last:border-b-0">
      <div className="text-sm text-slate-500 font-medium">
        {label}{required && <span className="text-rose-500"> *</span>}
      </div>
      <div>{children}</div>
    </div>
  );
}
